import { type ThemeState } from '@atlaskit/tokens';
import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { type FrameIdentity } from '@databases/database-state/state/bridge-data';
import { registerReliabilityExperimentMetadata } from '@databases/frontend-metrics/reliability-experiment-metadata';
import { setupGlobalLoadExperienceTimings } from '@databases/frontend-metrics/timings';
import { fetchIntlMessages } from '@databases/intl/fetch-intl-messages';
import { logFatalError } from '@databases/logs';
import { perf } from '@databases/logs/performance';
import { setAppMode, type AppMode } from '@databases/modes';
import { type ContainerOverrideOptions } from '@databases/service-container';

import { initialiseChangeboardingMessages } from '@canvas/changeboarding/use-changeboarding-messages';
import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { getDefaultLocale } from '@canvas/constants/localization/localization-types';
import { checkIndexeddbHealth } from '@canvas/indexeddb-state';
import { i18n } from '@canvas/intl';

import { addPolyfills } from './add-polyfills.ts';
import { configureSentry } from './configure-sentry.ts';
import { initializeAnalytics } from './initialize-analytics.ts';
import { initializeCanvasListeners } from './initialize-canvas-listeners.ts';
import { initializeConfluencePanelListeners } from './initialize-confluence-panel-listeners.ts';
import { initializeDatabasePaywallFeatures } from './initialize-database-paywall-features.ts';
import { initializeDatabaseRovoSelection } from './initialize-database-rovo-selection.ts';
import { initializeDatabaseTheme } from './initialize-database-theme.ts';
import { initializeFeatureFlags } from './initialize-feature-flags.ts';
import { initializeFieldTypesConfig } from './initialize-field-types-config.ts';
import { initializeSentry } from './initialize-sentry.ts';
import { initializeUserState } from './initialize-user-state.ts';
import { forwardHtmlSkeletonMarksToUFO } from './mount-skeleton.ts';
import { registerCoreEventListeners } from './register-core-event-listeners.ts';
import { registerDependenciesRequiredBeforeBootstrap } from './register-service-container-dependencies.ts';

export interface BootstrapOptions {
  appMode: AppMode;
  cloudId: string;
  spaceId: string;
  identity: FrameIdentity;
  referencing: boolean;
  themeState?: Pick<ThemeState, 'light' | 'dark' | 'colorMode' | 'typography'>;
  /**
   * Optional dependency overrides for the service container.
   *
   * Note: Intl and SelectionsActions are not included here as they are injected separately.
   * Intl is injected during bootstrap, and SelectionsActions is registered via
   * initializeRecyclableSelectionsMachine after bootstrap completes.
   */
  dependencies: Omit<ContainerOverrideOptions, 'Intl' | 'SelectionsActions'>;
}

/**
 * Bootstraps the databases application. This initializes all the core dependencies needed to run the application.
 * This returns the `bootstrapApplication` method, which can only be invoked once this is to force app initialization
 * with Sentry first.
 */
export function initializeBootstrapping() {
  perf.time('app-initialization');

  setupGlobalLoadExperienceTimings();
  initializeSentry();
  addPolyfills();

  const applyDependencyOverrides = registerDependenciesRequiredBeforeBootstrap();

  return async function bootstrapApplication({
    appMode,
    cloudId,
    dependencies,
    identity,
    spaceId,
    themeState,
  }: BootstrapOptions) {
    try {
      GlobalPageLoadExperience.startPageLoad('pending', true, performance.now());
      const cleanupSkeletonMarksObserver = forwardHtmlSkeletonMarksToUFO();

      configureSentry({ cloudId, userId: identity.accountId });

      setAppMode(appMode);
      registerCoreEventListeners();

      const locale = identity.locale ?? getDefaultLocale();

      await Promise.all([
        fetchIntlMessages(locale),
        initializeFeatureFlags({ cloudId, userId: identity.accountId }),
      ]);
      registerReliabilityExperimentMetadata();

      applyDependencyOverrides({ ...dependencies, Intl: () => i18n.intl });

      initializeDatabaseTheme(themeState);
      initializeFieldTypesConfig({
        'databases-frontend-live-doc': isFeatureFlagEnabled(
          'databases-frontend-live-doc',
        ),
      });
      initializeAnalytics({ cloudId, spaceId, accountId: identity.accountId });
      initializeConfluencePanelListeners();
      initializeCanvasListeners();
      initializeDatabasePaywallFeatures();
      initializeDatabaseRovoSelection();
      initializeUserState({ identity });
      initialiseChangeboardingMessages();

      checkIndexeddbHealth().catch((e) => {
        console.error('Error initializing indexedDB health check:', e);
      });

      cleanupSkeletonMarksObserver();
    } catch (e) {
      if (e instanceof Error) {
        logFatalError(e);
      }

      throw e;
    }
  };
}
